package songwritersnotebook.app.billing

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.auth
import dev.gitlive.firebase.firestore.firestore
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.filter
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.launch
import kotlinx.coroutines.withTimeoutOrNull
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.booleanOrNull
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.doubleOrNull
import kotlinx.serialization.json.encodeToJsonElement
import kotlinx.serialization.json.longOrNull
import kotlinx.serialization.json.put

@Serializable
private data class StripeError(val message: String? = null)

@Serializable
private data class StripeCustomerRequest(val email: String)

@Serializable
private data class StripeCheckoutSessionRequest(
    val price: String,
    val success_url: String,
    val cancel_url: String,
    val mode: String,
    val allow_promotion_codes: Boolean,
    val metadata: Map<String, String> = emptyMap()
)

actual class BillingClientWrapper actual constructor(private val scope: CoroutineScope) {
    actual companion object {
        // These constants are used for internal identification
        actual val TIER_HOBBYIST_ID: String = "hobbyist" 
        actual val TIER_STUDIO_ID: String = "studio"
        actual val AI_CREDIT_PRODUCT_IDS: List<String> = listOf(
            "ai_credits_100",
            "ai_credits_500",
            "ai_credits_1500"
        )

        // STRIPE PRICE ID MAPPINGS
        private const val STRIPE_STUDIO_MONTHLY = "price_1T19LB9u04zOJSew9H2d92LA"
        private const val STRIPE_STUDIO_YEARLY = "price_1T19LB9u04zOJSewoFGYWaPk"
        private const val STRIPE_CREDITS_100 = "price_1T19LF9u04zOJSewUG9Hobww"
        private const val STRIPE_CREDITS_500 = "price_1T19LI9u04zOJSewnsCnKDlG"
        private const val STRIPE_CREDITS_1500 = "price_1T19LJ9u04zOJSewokqkyhSf"
    }

    private val db = Firebase.firestore

    private val _productDetailsState = MutableStateFlow(ProductDetailsState(loadingState = LoadingState.LOADING))
    actual val productDetailsState: StateFlow<ProductDetailsState> = _productDetailsState.asStateFlow()

    private val _activeSubscription = MutableStateFlow(SubscriptionTier.FREE)
    actual val activeSubscription: StateFlow<SubscriptionTier> = _activeSubscription.asStateFlow()

    private val _billingError = MutableStateFlow<String?>(null)
    actual val billingError: StateFlow<String?> = _billingError.asStateFlow()

    private val _isProcessing = MutableStateFlow(false)
    actual val isProcessing: StateFlow<Boolean> = _isProcessing.asStateFlow()

    private val _consumablePurchase = MutableStateFlow(0)
    actual val consumablePurchase: Flow<Int> = _consumablePurchase

    init {
        println("Billing: BillingClientWrapper created.")
        // Pre-populate product info for Web so the UI displays the cards
        setupWebProductDetails()
    }

    private fun setupWebProductDetails() {
        if (_productDetailsState.value.loadingState == LoadingState.SUCCESS) return
        
        println("Billing: Setting up web product details...")
        val studioInfo = ProductInfo(
            productId = TIER_STUDIO_ID,
            name = "Studio Tier",
            description = "Cloud Sync & Collaboration",
            subscriptionOfferDetails = listOf(
                SubscriptionOfferDetails(
                    basePlanId = "studio-monthly",
                    offerToken = "web-monthly",
                    pricingPhases = PricingPhases(listOf(PricingPhase(4990000, "$4.99")))
                ),
                SubscriptionOfferDetails(
                    basePlanId = "studio-yearly",
                    offerToken = "web-yearly",
                    pricingPhases = PricingPhases(listOf(PricingPhase(49990000, "$49.99")))
                )
            )
        )

        val creditPacks = listOf(
            ProductInfo("ai_credits_100", "100 AI Credits", "One-time purchase", OneTimeOfferDetails("$0.99")),
            ProductInfo("ai_credits_500", "500 AI Credits", "One-time purchase", OneTimeOfferDetails("$3.99")),
            ProductInfo("ai_credits_1500", "1500 AI Credits", "One-time purchase", OneTimeOfferDetails("$9.99"))
        )

        _productDetailsState.value = ProductDetailsState(
            loadingState = LoadingState.SUCCESS,
            productDetailsList = listOf(studioInfo) + creditPacks
        )
        println("Billing: Web product details populated.")
    }

    actual fun queryActivePurchases(userId: String?) {
        if (userId == null) {
            _activeSubscription.value = SubscriptionTier.FREE
            return
        }
        
        scope.launch {
            try {
                println("Billing: queryActivePurchases - Starting snapshot listener for customers/$userId/subscriptions")
                // Listen to the subscriptions subcollection created by the Stripe Extension
                db.collection("customers").document(userId).collection("subscriptions")
                    .snapshots
                    .collect { snapshot ->
                        println("Billing: Subscriptions snapshot received. Size: ${snapshot.documents.size}")
                        val hasActiveSub = snapshot.documents.any { doc ->
                            val status = doc.get<String?>("status")
                            val id = doc.id
                            println("Billing: Sub $id status: $status")
                            status == "active" || status == "trialing"
                        }
                        if (hasActiveSub) {
                            _activeSubscription.value = SubscriptionTier.STUDIO
                        }
                    }
            } catch (e: Exception) {
                println("Billing: ERROR in queryActivePurchases snapshots: $e")
                println("Billing: Error syncing with Stripe Firestore data: $e")
            }
        }
    }

    actual fun launchPurchaseFlow(activity: Any, product: ProductInfo, userId: String, targetBasePlanId: String?) {
        println("Billing: launchPurchaseFlow called for ${product.productId} (UserId: $userId)")
        
        val priceId = when (product.productId) {
            TIER_STUDIO_ID -> if (targetBasePlanId?.contains("yearly") == true) STRIPE_STUDIO_YEARLY else STRIPE_STUDIO_MONTHLY
            "ai_credits_100" -> STRIPE_CREDITS_100
            "ai_credits_500" -> STRIPE_CREDITS_500
            "ai_credits_1500" -> STRIPE_CREDITS_1500
            else -> {
                println("Billing: Unknown product ID ${product.productId}")
                return
            }
        }

        if (priceId.contains("ID_HERE")) {
            _billingError.value = "Stripe is not yet configured for this web build."
            return
        }

        println("Billing: Using Price ID $priceId")

        _isProcessing.value = true
        scope.launch {
            try {
                // 1. Ensure parent customer document exists (the extension needs this to exist)
                val customerRef = db.collection("customers").document(userId)
                try {
                    val email = Firebase.auth.currentUser?.email ?: ""
                    println("Billing: Ensuring customer document for $userId with email $email")
                    customerRef.set(StripeCustomerRequest(email), merge = true)
                } catch (e: Exception) {
                    println("Billing: Error ensuring customer document: ${e.message}")
                }

                // 2. Create checkout session IMMEDIATELY. 
                println("Billing: Creating checkout session document in customers/$userId/checkout_sessions...")
                val sessionRequest = StripeCheckoutSessionRequest(
                    price = priceId,
                    success_url = window.location.origin,
                    cancel_url = window.location.origin,
                    mode = if (AI_CREDIT_PRODUCT_IDS.contains(product.productId)) "payment" else "subscription",
                    allow_promotion_codes = true,
                    metadata = mapOf("priceId" to priceId)
                )

                val sessionRef = db.collection("customers")
                    .document(userId)
                    .collection("checkout_sessions")
                    .add(sessionRequest)
                
                println("Billing: Checkout session created with ID: ${sessionRef.id}. Waiting for Stripe URL...")

                // 3. Wait for the extension to process the session and provide a URL
                val checkoutUrl = withTimeoutOrNull(30000) { // 30 second timeout
                    db.collection("customers")
                        .document(userId)
                        .collection("checkout_sessions")
                        .document(sessionRef.id)
                        .snapshots
                        .map { 
                            val url = it.get<String?>("url")
                            val error = it.get<StripeError?>("error")
                            
                            if (error != null) {
                                println("Billing: Extension reported an error: ${error.message}")
                                throw Exception(error.message ?: "Unknown Stripe error")
                            }
                            
                            url
                        }
                        .filter { it != null }
                        .first()
                }

                if (checkoutUrl != null) {
                    println("Billing: Redirecting to Stripe: $checkoutUrl")
                    window.location.assign(checkoutUrl)
                } else {
                    println("Billing: TIMEOUT waiting for checkout URL.")
                    _billingError.value = "Stripe is taking a while. Please try again in a moment."
                    _isProcessing.value = false
                }
            } catch (e: Exception) {
                println("Billing: EXCEPTION: $e")
                _billingError.value = "Failed to initiate checkout: ${e.message}"
                _isProcessing.value = false
            }
        }
    }

    actual fun clearError() {
        _billingError.value = null
    }

    actual fun forceUpdateSubscriptionTier(tier: SubscriptionTier) {
        _activeSubscription.value = tier
    }

    actual fun reset() {
        _activeSubscription.value = SubscriptionTier.FREE
    }

    actual fun reinitialize() {
        setupWebProductDetails()
        // Force a re-query if we have a user, which helps after returning from a checkout redirect
        val currentUser = Firebase.auth.currentUser
        if (currentUser != null) {
            scope.launch {
                delay(2000) // Small buffer for Stripe extension to write the doc
                queryActivePurchases(currentUser.uid)
            }
        }
    }
}
